import { Button, EmptyState, SurfaceLoading, SurfaceStaleHint } from '@/shared/components';
import { CalendarLegend } from './CalendarLegend';
import type { CalendarReassignmentRequest } from './calendarReassignment';
import { CalendarLoadError, CalendarMobileHint } from './CalendarStates';
import { CalendarToolbar, type CalendarToolbarProps } from './CalendarToolbar';
import { PropertyCalendarGrid } from './PropertyCalendarGrid';
import type { useApartmentCalendar } from './useApartmentCalendar';
import type { CalendarRows } from './useCalendarRows';
import type { GuestAccess } from './bookingDetailsModel';
import type { CalendarView } from './useCalendarView';

interface Props {
  view: CalendarView;
  calendar: ReturnType<typeof useApartmentCalendar>;
  rows: CalendarRows;
  toolbar: Omit<CalendarToolbarProps, 'layout'>;
  today: string;
  editable: boolean;
  operations: boolean;
  /** Gastdaten wie der Server: Name und Telefon mit Kontakt-Recht, E-Mail mit vollen Gastdaten. */
  guestAccess: GuestAccess;
  onFreeDay: (propertyId: string, date: string, propertyName: string) => void;
  onReassignmentRequest: (request: CalendarReassignmentRequest) => void;
}

/**
 * Die Apartment-Karte der Belegung mit allen Seitenzuständen: lädt, Fehler mit Grund und „Erneut
 * laden“, veraltet nach misslungenem Nachladen (Raster bleibt), keine Apartments, keins gewählt.
 */
export function ApartmentsCard({ view, calendar, rows, toolbar, ...props }: Props) {
  const { data, error } = calendar;
  const ready = data !== undefined && data.properties.length > 0;
  return (
    <>
      <section
        aria-label="Belegung der Apartments"
        className="bg-card border-hairline rounded-tile shadow-float overflow-hidden border"
      >
        {!view.mobile && (
          <div className="border-hairline border-b px-[18px] py-3.5">
            <CalendarToolbar {...toolbar} />
          </div>
        )}
        {/* Scheitert nur das Nachladen, bleibt jeder Stand mit Hinweis stehen – auch „leer“. */}
        {data && error && (
          <SurfaceStaleHint onRetry={calendar.refetch} retrying={calendar.isFetching} />
        )}
        {!data && error ? (
          <CalendarLoadError
            error={error}
            onRetry={calendar.refetch}
            retrying={calendar.isFetching}
          />
        ) : !data ? (
          <SurfaceLoading label="Belegung wird geladen" />
        ) : data.properties.length === 0 ? (
          <EmptyState
            icon="Building"
            title="Noch keine Apartments"
            description="Lege zuerst ein Apartment an, dann erscheint es in der Belegung."
          />
        ) : rows.layouts.length === 0 ? (
          <div className="flex flex-col items-center gap-3 p-8 text-center">
            <p className="text-ink-soft text-sm">Kein Apartment gewählt.</p>
            <Button
              variant="secondary"
              className="min-h-11"
              onClick={() => toolbar.onSelectedPropertiesChange(null)}
            >
              Alle Apartments zeigen
            </Button>
          </div>
        ) : (
          <PropertyCalendarGrid
            rows={rows}
            allRows={data.properties}
            addressByPropertyId={calendar.addressByPropertyId}
            guestAccess={props.guestAccess}
            operations={props.operations}
            mobileDensity={view.density}
            today={props.today}
            canReassign={props.editable}
            onReassignmentRequest={props.onReassignmentRequest}
            onFreeCellClick={props.editable ? props.onFreeDay : undefined}
          />
        )}
      </section>
      {ready && view.mobile && <CalendarMobileHint manager={props.editable} />}
      {ready && <CalendarLegend {...rows.legend} manager={props.editable} compact={view.mobile} />}
    </>
  );
}
